import { useCallback } from 'react';
import { useMatchRoute as useMatchRouteLegacy } from '@tanstack/react-location';
import { useIntakeFormShallowStore } from '@frontend/api-intake-form';
import { useLocation, useMatchRoute } from '@frontend/weave-router';
import { INTAKE_FORM_DEMO_PATHS, INTAKE_FORM_PATHS, IntakeFormRouteKey } from '../constants';
import { injectParams } from '../utils/injectParams';

export type GetFormPathFnType = (
  routeName: IntakeFormRouteKey,
  params?: Record<string, unknown>,
  query?: Record<string, unknown>
) => string;

type GetMatchRouteFnType = (matchLocation: {
  routeName: keyof typeof INTAKE_FORM_PATHS;
  fuzzy?: boolean;
}) => Record<string, unknown> | null;

type UseIntakeFormPathsType = {
  isDemoRoute: boolean;
  getFormPath: GetFormPathFnType;
  getMatchRoute: GetMatchRouteFnType;
};

/**
 *
 * This hook is used in contexts of the new weave router and the legacy react-location router, so our match routes need to check both routers
 */
export const useIntakeFormPaths = (): UseIntakeFormPathsType => {
  const current = useLocation();
  const matchRoute = useMatchRoute();
  const matchRouteLegacy = useMatchRouteLegacy();
  const { selectedIntakeFormLocationId } = useIntakeFormShallowStore('selectedIntakeFormLocationId');

  const isDemoRoute = current.pathname.startsWith(INTAKE_FORM_DEMO_PATHS.root);

  const getFormPath: GetFormPathFnType = useCallback(
    (routeName, params = {}, query = {}) =>
      injectParams(
        (isDemoRoute ? INTAKE_FORM_DEMO_PATHS : INTAKE_FORM_PATHS)[routeName],
        { locationId: selectedIntakeFormLocationId, ...params },
        query
      ),
    [selectedIntakeFormLocationId, isDemoRoute]
  );

  const getMatchRoute = useCallback<GetMatchRouteFnType>(
    ({ routeName, fuzzy }) => {
      const route = (isDemoRoute ? INTAKE_FORM_DEMO_PATHS : INTAKE_FORM_PATHS)[routeName];
      const match = matchRoute({
        to: route as string,
        fuzzy: fuzzy,
      }) as Record<string, unknown>;
      const matchLegacy = matchRouteLegacy({
        to: route as string,
        fuzzy: fuzzy,
      });
      return matchLegacy ?? match ?? null;
    },
    [isDemoRoute, matchRoute, matchRouteLegacy]
  );

  return {
    isDemoRoute,
    getFormPath,
    getMatchRoute,
  };
};
